import { computed } from 'vue';
import { useStore } from 'vuex';
import { ALERTS_CONFIGS_CONSTS } from '@modules/alerts/consts';
import { isNullable } from '@utils/commonUtils';
import { GET_ALL_PRODUCT_IMAGES_CTA_DIMENSIONS } from '../../../types/getters';

// apply product id with a leading dash
const applyProductId = (productId) => `-${productId}`;

/**
 * Caches alert values when propName is defined and returns the in the next calls
 * @param {string} productId
 * @param {import('vue').ComputedRef<string>} name
 * @param {import('vue').ComputedRef<string>} action
 * @return {{
 *    dataProps: Object,
 *    teleportTo: import('vue').ComputedRef<string>,
 *    classList: import('vue').ComputedRef<string[]>,
 *    show: import('vue').ComputedRef<boolean>
 * }}
 */
export function useAlerts({
  productId, name, action,
}) {
  const store = useStore();

  /** Offset Props * */
  const ctaButtonsDimensions = computed(
    () => store.getters[`productImagesCtaStore/${GET_ALL_PRODUCT_IMAGES_CTA_DIMENSIONS}`]?.[productId],
  );
  const ctaButtonsHeight = computed(() => ctaButtonsDimensions.value?.height);

  const offset = computed(() => {
    if (name.value === 'marxent' && action.value === 'error') {
      return ctaButtonsHeight.value + 24;
    }

    return null;
  });

  const offsetIsInvalid = computed(() => isNullable(offset.value) || Number.isNaN(offset.value));

  /** Toast Props * */
  const configs = computed(() => ALERTS_CONFIGS_CONSTS?.[name.value]?.[action.value] ?? {});

  // toast props
  const dataProps = computed(() => ({
    // message, placement, duration, etc...
    ...(configs.value?.data ?? {}),
    // offset
    ...(!offsetIsInvalid.value && { offset: offset.value }),
  }));

  /** Teleport Props * */
  // default -> 'body'
  const teleportTo = computed(() => {
    const { prefix = '', selector = '', appendProductId = false } = configs.value?.teleport ?? {};
    return selector ? `${prefix}${selector}${appendProductId ? applyProductId(productId) : ''}` : 'body';
  });

  return {
    dataProps,
    teleportTo,
    classList: computed(() => configs.value?.classList ?? []),
    show: computed(() => !!(name.value && action.value)),
  };
}

export default useAlerts;
